import { useCallback } from "react";

import { useAppState } from "@/components/AppStateProvider";
import Box from "@/components/Box";
import CircleButton from "@/components/CircleButton";
import GridTestProductCard from "@/components/GridTestProductCard";
import StackedTestProductCard from "@/components/StackedTestProductCard";
import { useTestProductModalContext } from "@/contexts/TestProductModalContext";
import { useOrganisationBasketContext } from "@/contexts/organisations/OrganisationBasketContext";
import { useOrganisationContext } from "@/contexts/organisations/OrganisationContext";
import { COMING_SOON, OUT_OF_STOCK } from "@/core/constants";
import useWindowSize from "@/hooks/use-window-size";
import { GilroyFontCurrency } from "@/tpo/Currency";
import Group from "@/tpo/Group";
import Stack from "@/tpo/Stack";
import ButtonV2 from "@/v2/Buttons";

import { CMP_PERMISSION_PURCHASE_PRODUCTS } from "../constants";

export default function FeaturedTests({ children, testProducts }) {
  const { organisation } = useOrganisationContext();
  const { basket, hasBasketGotProduct, addProductToBasket, removeProductFromBasket } =
    useOrganisationBasketContext();
  const { selectTestProductId } = useTestProductModalContext();
  const { setPractitionerBasketOpen: setBasketOpen } = useAppState();
  const { width } = useWindowSize();

  const permissions = organisation?.permissions || [];
  const useGridCard = !width || width <= 960;

  const handleAddToBasket = useCallback(
    testProduct => {
      if (!basket) return;
      if (
        basket.multiplePatientsEnabled &&
        testProduct.isCyrex &&
        !hasBasketGotProduct(testProduct.id)
      ) {
        return;
      }
      const allowMoreThanOne = basket?.purchaseAsStock || basket?.multiplePatientsEnabled;
      if (testProduct.options?.length || (!allowMoreThanOne && testProduct.addons?.length)) {
        selectTestProductId(testProduct.id);
        return;
      }
      addProductToBasket(testProduct.id);
      selectTestProductId(null);
      setBasketOpen(true);
    },
    [addProductToBasket, selectTestProductId, setBasketOpen, basket, hasBasketGotProduct]
  );

  const handleRemoveFromBasket = useCallback(
    testProduct => {
      const line = basket.productLineItems.find(line => line.product.id === testProduct.id);
      // clinic location should not be present within the context this function is called
      // but add anyway to be explicit
      removeProductFromBasket(line.product.id, line.productOption?.id, line.clinicLocation?.id);
    },
    [removeProductFromBasket, basket]
  );

  return (
    <Stack gap={20} pt={60} width="100%" maxWidth={720}>
      <Box fontFamily="gilroyBold" fontSize={[32, 32, 44]}>
        Featured Tests
      </Box>
      {children}
      {testProducts.map(testProduct => {
        const allowMoreThanOne = basket?.purchaseAsStock || basket?.multiplePatientsEnabled;

        let addText = "Add";
        if (testProduct.options?.length || (!allowMoreThanOne && testProduct.addons?.length)) {
          addText = "Options";
        } else if (!allowMoreThanOne && hasBasketGotProduct(testProduct.id)) {
          addText = "Added";
        }

        if (testProduct.status === OUT_OF_STOCK) {
          addText = "Out of stock";
        } else if (testProduct.status === COMING_SOON) {
          addText = "Coming soon";
        }

        if (!testProduct.isStudentAllowed) {
          addText = "Not available";
        }

        const onAdd =
          allowMoreThanOne || (!allowMoreThanOne && !hasBasketGotProduct(testProduct.id))
            ? e => {
                e.onAdd = true;
                if (allowMoreThanOne || !hasBasketGotProduct(testProduct.id)) {
                  handleAddToBasket(testProduct);
                }
              }
            : undefined;

        const cyrexBlockedForMultiPatient =
          basket?.multiplePatientsEnabled &&
          testProduct.isCyrex &&
          !hasBasketGotProduct(testProduct.id);

        const addDisabled =
          testProduct.status === OUT_OF_STOCK ||
          testProduct.status === COMING_SOON ||
          !testProduct.isStudentAllowed ||
          cyrexBlockedForMultiPatient ||
          (!allowMoreThanOne && hasBasketGotProduct(testProduct.id)) ||
          !permissions.includes(CMP_PERMISSION_PURCHASE_PRODUCTS);

        const onRemove =
          !allowMoreThanOne && hasBasketGotProduct(testProduct.id)
            ? e => {
                e.onRemove = true;
                handleRemoveFromBasket(testProduct);
              }
            : undefined;

        const showTradeOnCard =
          testProduct.tradeCurrentPrice != null && Number(testProduct.tradeCurrentPrice) !== 0;

        const priceSection = (
          <Group
            flexWrap="wrap"
            justifyContent="space-between"
            alignItems="center"
            mt="auto"
            gap={28}
          >
            <Stack>
              {showTradeOnCard ? (
                <Group>
                  {testProduct.tradeFullPrice !== undefined &&
                    testProduct.tradeFullPrice > testProduct.tradeCurrentPrice && (
                      <GilroyFontCurrency
                        value={testProduct.tradeFullPrice}
                        symbol={basket?.currencySymbol}
                        data-testid="testProductListCard:tradeFullPrice"
                        strikethrough
                        color="dark"
                      />
                    )}
                  <GilroyFontCurrency
                    value={testProduct.tradeCurrentPrice}
                    symbol={basket?.currencySymbol}
                    data-testid="testProductListCard:tradeCurrentPrice"
                    color={
                      testProduct.tradeFullPrice !== undefined &&
                      testProduct.tradeFullPrice > testProduct.tradeCurrentPrice
                        ? "red"
                        : "dark"
                    }
                  />
                </Group>
              ) : null}
              {showTradeOnCard ? (
                <GilroyFontCurrency
                  prefix="RRP"
                  color="midGrey"
                  fontSize={[16, 16, 18]}
                  decimalFontSize={[16, 16, 18]}
                  symbol={basket?.currencySymbol}
                  value={testProduct.exvatFullPrice}
                  data-testid="testProductListCard:retailFullPrice"
                />
              ) : null}
            </Stack>
            {onAdd && (
              <ButtonV2
                withChevron
                onClick={onAdd}
                disabled={addDisabled}
                color={addDisabled ? "midGrey" : "green"}
                size={["sm", "sm", "md"]}
              >
                {addText}
              </ButtonV2>
            )}
            {onRemove && (
              <ButtonV2 onClick={onRemove} color="red" withChevron size={["sm", "sm", "md"]}>
                Remove
              </ButtonV2>
            )}
          </Group>
        );

        const cardProps = {
          withBorder: true,
          onClick: e => {
            if (e.onAdd || e.onRemove) return;
            selectTestProductId(testProduct.id);
          },
          "data-component-name": "TestProductListCard"
        };

        if (useGridCard) {
          return (
            <GridTestProductCard.Card key={testProduct.id} {...cardProps}>
              <GridTestProductCard.Header
                iconSize={[40, 40, 80]}
                iconType={testProduct.content.iconType}
              >
                <CircleButton />
              </GridTestProductCard.Header>
              <Stack gap={16} pb={60} mt={16}>
                <GridTestProductCard.Content
                  id={testProduct.id}
                  name={testProduct.name}
                  addons={testProduct.addons}
                  laboratory={testProduct.laboratory}
                  shortDescription={testProduct.content.shortDescription}
                  testType={testProduct.content.testType}
                  categories={testProduct.content.categories || []}
                  centrifuge={testProduct.content.centrifuge}
                />
              </Stack>
              {priceSection}
            </GridTestProductCard.Card>
          );
        }

        return (
          <StackedTestProductCard.Card key={testProduct.id} {...cardProps}>
            <Stack gap={16} pb={60}>
              <StackedTestProductCard.Header
                iconSize={[40, 40, 80]}
                iconType={testProduct.content.iconType}
                id={testProduct.id}
                name={testProduct.name}
                addons={testProduct.addons}
                laboratory={testProduct.laboratory}
                centrifuge={testProduct.content.centrifuge}
              >
                <CircleButton />
              </StackedTestProductCard.Header>
              <StackedTestProductCard.Content
                shortDescription={testProduct.content.shortDescription}
                testType={testProduct.content.testType}
                categories={testProduct.content.categories || []}
              />
            </Stack>
            {priceSection}
          </StackedTestProductCard.Card>
        );
      })}
    </Stack>
  );
}
